import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import {
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { JRWA_GMINY, IJrwaNode } from "data/jrwa_gminy";
import styles from "./styles.module.scss";

// ── Persistence ──────────────────────────────────────────────────────────────

const STORAGE_KEY = "jrwa_tree_v2";

const loadTree = (): IJrwaNode[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveTree = (nodes: IJrwaNode[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nodes));
  } catch {
    // storage full — ignore
  }
};

// ── Tree operations ───────────────────────────────────────────────────────────

const collectAllIds = (nodes: IJrwaNode[]): Set<string> => {
  const ids = new Set<string>();
  const visit = (n: IJrwaNode) => {
    if (n.children.length > 0) ids.add(n.id);
    n.children.forEach(visit);
  };
  nodes.forEach(visit);
  return ids;
};

const filterTree = (nodes: IJrwaNode[], q: string): [IJrwaNode[], Set<string>] => {
  const expanded = new Set<string>();
  const lower = q.toLowerCase();

  const filterNode = (n: IJrwaNode): IJrwaNode | null => {
    const filteredChildren = n.children
      .map(filterNode)
      .filter(Boolean) as IJrwaNode[];
    const selfMatch =
      n.label.toLowerCase().includes(lower) ||
      n.code.toLowerCase().includes(lower);

    if (selfMatch || filteredChildren.length > 0) {
      if (filteredChildren.length > 0) expanded.add(n.id);
      return { ...n, children: filteredChildren };
    }
    return null;
  };

  const result = nodes.map(filterNode).filter(Boolean) as IJrwaNode[];
  return [result, expanded];
};

const insertNode = (
  tree: IJrwaNode[],
  parentId: string,
  newNode: IJrwaNode,
): IJrwaNode[] =>
  tree.map((n) => {
    if (n.id === parentId) return { ...n, children: [...n.children, newNode] };
    return { ...n, children: insertNode(n.children, parentId, newNode) };
  });

const updateLabel = (
  tree: IJrwaNode[],
  id: string,
  label: string,
): IJrwaNode[] =>
  tree.map((n) => {
    if (n.id === id) return { ...n, label };
    return { ...n, children: updateLabel(n.children, id, label) };
  });

const removeNode = (tree: IJrwaNode[], id: string): IJrwaNode[] =>
  tree
    .filter((n) => n.id !== id)
    .map((n) => ({ ...n, children: removeNode(n.children, id) }));

const findNode = (tree: IJrwaNode[], id: string): IJrwaNode | null => {
  for (const n of tree) {
    if (n.id === id) return n;
    const found = findNode(n.children, id);
    if (found) return found;
  }
  return null;
};

// ── TreeNode component ────────────────────────────────────────────────────────

interface TreeNodeProps {
  node: IJrwaNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onAdd: (parentId: string, parentLabel: string) => void;
  onEdit: (id: string, label: string) => void;
  onDelete: (id: string, label: string) => void;
  searchActive: boolean;
}

const TreeNode = ({
  node,
  depth,
  expanded,
  onToggle,
  onAdd,
  onEdit,
  onDelete,
  searchActive,
}: TreeNodeProps) => {
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.id);

  return (
    <div className={styles.nodeWrapper}>
      <div
        className={`${styles.nodeRow} ${depth === 0 ? styles.nodeRowL1 : ""} ${
          depth === 1 ? styles.nodeRowL2 : ""
        }`}
        style={{ paddingLeft: 8 + depth * 20 }}
      >
        {/* Expand icon */}
        <span
          className={styles.chevron}
          onClick={() => hasChildren && onToggle(node.id)}
          style={{ visibility: hasChildren ? "visible" : "hidden" }}
        >
          {isOpen || searchActive ? (
            <ExpandMoreIcon sx={{ fontSize: 16 }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 16 }} />
          )}
        </span>

        {/* Code chip */}
        <span className={`${styles.codeChip} ${node.custom ? styles.codeChipCustom : ""}`}>
          {node.code}
        </span>

        {/* Label */}
        <span className={`${styles.nodeLabel} ${node.custom ? styles.nodeLabelCustom : ""}`}>
          {node.label}
        </span>

        {/* Actions (shown on hover via CSS) */}
        <span className={styles.nodeActions}>
          <Tooltip title="Dodaj podkategorię" placement="top" arrow>
            <span
              className={styles.actionBtn}
              onClick={(e) => {
                e.stopPropagation();
                onAdd(node.id, node.label);
              }}
            >
              <AddIcon sx={{ fontSize: 13 }} />
            </span>
          </Tooltip>
          {node.custom && (
            <>
              <Tooltip title="Edytuj hasło" placement="top" arrow>
                <span
                  className={styles.actionBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(node.id, node.label);
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 13 }} />
                </span>
              </Tooltip>
              <Tooltip title="Usuń" placement="top" arrow>
                <span
                  className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(node.id, node.label);
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                </span>
              </Tooltip>
            </>
          )}
        </span>
      </div>

      {/* Children */}
      {hasChildren && (isOpen || searchActive) && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              onAdd={onAdd}
              onEdit={onEdit}
              onDelete={onDelete}
              searchActive={searchActive}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const JrwaTab = () => {
  const [tree, setTree] = useState<IJrwaNode[]>(
    () => loadTree() ?? JSON.parse(JSON.stringify(JRWA_GMINY)),
  );

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  // Dialog states
  const [addDialog, setAddDialog] = useState<{
    parentId: string;
    parentLabel: string;
  } | null>(null);
  const [editDialog, setEditDialog] = useState<{
    id: string;
  } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    id: string;
    label: string;
  } | null>(null);

  // Dialog inputs
  const [newCode, setNewCode] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [editLabel, setEditLabel] = useState("");

  const persist = useCallback((newTree: IJrwaNode[]) => {
    setTree(newTree);
    saveTree(newTree);
  }, []);

  // ── Filtered view ─────────────────────────────────────────────────────────

  const [filteredTree, searchExpanded] = useMemo(() => {
    if (!search.trim()) return [tree, new Set<string>()] as const;
    return filterTree(tree, search.trim());
  }, [tree, search]);

  const searchActive = search.trim().length > 0;

  // ── Toggle expand ─────────────────────────────────────────────────────────

  const handleToggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleExpandAll = () => setExpanded(collectAllIds(tree));
  const handleCollapseAll = () => setExpanded(new Set());

  // ── Add ───────────────────────────────────────────────────────────────────

  const openAdd = (parentId: string, parentLabel: string) => {
    setAddDialog({ parentId, parentLabel });
    setNewCode("");
    setNewLabel("");
  };

  const confirmAdd = () => {
    if (!addDialog || !newLabel.trim()) return;
    const id = newCode.trim() || `custom_${Date.now()}`;
    const node: IJrwaNode = {
      id,
      code: newCode.trim() || "—",
      label: newLabel.trim(),
      children: [],
      custom: true,
    };
    const newTree = insertNode(tree, addDialog.parentId, node);
    persist(newTree);
    setExpanded((prev) => new Set([...prev, addDialog.parentId]));
    setAddDialog(null);
  };

  // ── Edit ──────────────────────────────────────────────────────────────────

  const openEdit = (id: string, label: string) => {
    setEditDialog({ id });
    setEditLabel(label);
  };

  const confirmEdit = () => {
    if (!editDialog || !editLabel.trim()) return;
    persist(updateLabel(tree, editDialog.id, editLabel.trim()));
    setEditDialog(null);
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const confirmDelete = () => {
    if (!deleteConfirm) return;
    persist(removeNode(tree, deleteConfirm.id));
    setDeleteConfirm(null);
  };

  // ── Reset to original ─────────────────────────────────────────────────────

  const handleReset = () => {
    const fresh = JSON.parse(JSON.stringify(JRWA_GMINY));
    persist(fresh);
    setExpanded(new Set());
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const displayTree = searchActive ? filteredTree : tree;
  const displayExpanded = searchActive ? searchExpanded : expanded;

  return (
    <div className={styles.wrapper}>
      {/* ── Toolbar ── */}
      <div className={styles.toolbar}>
        <TextField
          inputRef={searchRef}
          size="small"
          placeholder="Szukaj po symbolu lub haśle klasyfikacyjnym…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={styles.searchBox}
          InputProps={{
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  ✕
                </IconButton>
              </InputAdornment>
            ) : (
              <InputAdornment position="end">
                <SearchIcon fontSize="small" sx={{ color: "rgba(0,0,0,0.3)" }} />
              </InputAdornment>
            ),
          }}
        />
        <div className={styles.toolbarRight}>
          <Tooltip title="Rozwiń wszystko">
            <span>
              <IconButton size="small" onClick={handleExpandAll} disabled={searchActive}>
                <UnfoldMoreIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Zwiń wszystko">
            <span>
              <IconButton size="small" onClick={handleCollapseAll} disabled={searchActive}>
                <UnfoldLessIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Przywróć oryginalną taksonomię (usuwa własne zmiany)">
            <span>
              <IconButton size="small" onClick={handleReset} sx={{ color: "rgba(0,0,0,0.35)" }}>
                <span style={{ fontSize: 11, fontWeight: 600 }}>↺</span>
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>

      {/* ── Legend ── */}
      <div className={styles.legend}>
        <span className={`${styles.legendDot} ${styles.legendDotSystem}`} />
        oficjalne JRWA
        <span className={`${styles.legendDot} ${styles.legendDotCustom}`} style={{ marginLeft: 16 }} />
        własne rozszerzenia
        <span className={styles.legendHint}>
          — ikona <AddIcon sx={{ fontSize: 11, verticalAlign: "middle" }} /> pojawia się po najechaniu na wiersz
        </span>
      </div>

      {/* ── Tree ── */}
      <div className={styles.treeContainer}>
        {displayTree.length === 0 ? (
          <div className={styles.emptyMsg}>
            Brak wyników dla „{search}"
          </div>
        ) : (
          displayTree.map((node) => (
            <TreeNode
              key={node.id}
              node={node}
              depth={0}
              expanded={displayExpanded}
              onToggle={handleToggle}
              onAdd={openAdd}
              onEdit={openEdit}
              onDelete={(id, label) => setDeleteConfirm({ id, label })}
              searchActive={searchActive}
            />
          ))
        )}
      </div>

      {/* ── Dialog: dodaj podkategorię ── */}
      <Dialog open={!!addDialog} onClose={() => setAddDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600, pb: 1 }}>
          Dodaj podkategorię
        </DialogTitle>
        <DialogContent>
          <div className={styles.dialogHint}>
            Kategoria nadrzędna:{" "}
            <strong>{addDialog?.parentLabel}</strong>
          </div>
          <TextField
            label="Symbol (opcjonalny)"
            size="small"
            fullWidth
            value={newCode}
            onChange={(e) => setNewCode(e.target.value)}
            sx={{ mt: 1.5, mb: 2 }}
            placeholder="np. 0001a"
            helperText="Jeśli pusty, zostanie wygenerowany automatycznie"
          />
          <TextField
            label="Hasło klasyfikacyjne *"
            size="small"
            fullWidth
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && confirmAdd()}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddDialog(null)} color="inherit" size="small">
            Anuluj
          </Button>
          <Button
            onClick={confirmAdd}
            variant="contained"
            size="small"
            disabled={!newLabel.trim()}
          >
            Dodaj
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: edytuj ── */}
      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600, pb: 1 }}>
          Edytuj hasło klasyfikacyjne
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Hasło klasyfikacyjne *"
            size="small"
            fullWidth
            value={editLabel}
            onChange={(e) => setEditLabel(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && confirmEdit()}
            autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialog(null)} color="inherit" size="small">
            Anuluj
          </Button>
          <Button
            onClick={confirmEdit}
            variant="contained"
            size="small"
            disabled={!editLabel.trim()}
          >
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: usuń ── */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600, pb: 1 }}>
          Usuń kategorię
        </DialogTitle>
        <DialogContent>
          Czy na pewno usunąć kategorię{" "}
          <strong>„{deleteConfirm?.label}"</strong>?
          <br />
          <span style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", marginTop: 4, display: "block" }}>
            Zostaną usunięte również wszystkie jej podkategorie.
          </span>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)} color="inherit" size="small">
            Anuluj
          </Button>
          <Button onClick={confirmDelete} variant="contained" color="error" size="small">
            Usuń
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default JrwaTab;
